import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, CheckCircle, Award, Sparkles, X
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const TIERS = [
  {
    name: "Aspiring",
    tagline: "Begin Your Journey",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/tx2ugvu9_aspiring.jpeg",
    color: "#94A3B8",
    description: "For organisations starting out on their conscious enterprise journey. Establish a baseline, identify quick wins, and lay the foundation for measurable change.",
    duration: "30 days",
    investment: "Most accessible",
    requirements: [
      "Complete the 80-question CNESS assessment",
      "Submit baseline documentation",
      "Light evaluator review",
    ],
    benefits: [
      "Aspiring CNESS digital badge",
      "Listing in the global directory",
      "Personalised gap-analysis report",
      "Access to community resources",
      "Annual renewal pathway",
    ],
    bestFor: "Startups, micro-enterprises, and organisations new to responsible business frameworks.",
  },
  {
    name: "Inspired",
    tagline: "Commit to Excellence",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/ko1qopap_inspired.jpeg",
    color: "#3B82F6",
    description: "For companies committed to embedding conscious practice across the six pillars. Includes structured coaching, interim recognition, and full evaluator review.",
    duration: "45–60 days",
    investment: "Mid-tier",
    requirements: [
      "Complete the 80-question assessment",
      "Provide pillar-level evidence portfolio",
      "Independent evaluator scoring",
      "Three coaching sessions with a CNESS mentor",
    ],
    benefits: [
      "Inspired CNESS premium badge",
      "Priority directory placement",
      "Marketing co-branding kit",
      "Partner networking events",
      "Annual progress review",
      "Detailed pillar-by-pillar scorecard",
    ],
    bestFor: "Growing SMEs, mid-market enterprises, and any team ready to operationalise responsible practice.",
  },
  {
    name: "Luminary",
    tagline: "Lead the Movement",
    badge: "https://customer-assets.emergentagent.com/job_a200484a-fa79-4033-96d7-7cec268596ba/artifacts/td0i3ea7_luminary.jpeg",
    color: "#FDB813",
    description: "Reserved for the global leaders who exemplify conscious enterprise. Recognised internationally and invited to shape the next wave of responsible business.",
    duration: "60–90 days",
    investment: "Premium",
    requirements: [
      "Comprehensive multi-evaluator review",
      "Executive interviews and on-site (or virtual) audit",
      "Stakeholder reference checks",
      "Demonstrated leadership across all six pillars",
    ],
    benefits: [
      "Luminary signature badge & certificate",
      "Featured case study on CNESS",
      "Speaking invitations at CNESS summits",
      "Advisory board eligibility",
      "Premium marketing & PR support",
      "Annual strategic strategy review",
      "Global thought-leadership publications",
    ],
    bestFor: "Industry pacesetters, listed enterprises, and global organisations setting the standard for others.",
  },
];

const COMPARISON = [
  ["Independent evaluator review", "Light", "Full", "Multi-evaluator + on-site audit"],
  ["Coaching sessions included", "—", "3 sessions", "Unlimited"],
  ["Directory placement", "Standard", "Priority", "Featured / Spotlight"],
  ["Marketing co-branding", "—", "Standard kit", "Premium kit + PR"],
  ["Annual review", "Self-serve", "Guided review", "Strategic review with leadership"],
  ["Speaking & advisory access", "—", "—", "Yes"],
];

const CertificationTiersPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#FFFBEB] to-white" data-testid="cert-tiers-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Award className="w-4 h-4 text-[#FDB813]" />
              <span className="text-sm font-medium text-[#1E3A5F]">Certification Tiers</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
              Three tiers. <span className="text-[#FDB813]">One framework.</span><br />
              The right step for every stage.
            </h1>
            <p className="text-lg text-gray-600">
              Whether you are getting started, committing to scale, or already setting the global standard —
              CNESS has a tier that matches your ambition.
            </p>
          </div>
        </div>
      </section>

      {/* Tier Detail Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {TIERS.map((tier, i) => (
            <div
              key={tier.name}
              data-testid={`cert-tier-detail-${tier.name.toLowerCase()}`}
              className="grid lg:grid-cols-3 gap-8 items-start bg-white border border-gray-100 rounded-3xl p-8 lg:p-10 shadow-sm hover:shadow-lg transition"
            >
              {/* Left: badge + name */}
              <div className="lg:col-span-1 text-center lg:text-left">
                <img src={tier.badge} alt={`${tier.name} CNESS Badge`} className="w-32 h-32 object-contain mx-auto lg:mx-0 mb-6" />
                <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: tier.color }}>
                  Tier {i + 1}
                </p>
                <h2 className="text-3xl font-bold text-[#1E3A5F] mb-1">{tier.name}</h2>
                <p className="text-base font-medium" style={{ color: tier.color }}>{tier.tagline}</p>
                <p className="text-gray-600 mt-4 leading-relaxed">{tier.description}</p>

                <div className="grid grid-cols-2 gap-3 mt-6">
                  <div className="bg-[#F8FAFC] rounded-xl p-3">
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="font-semibold text-[#1E3A5F]">{tier.duration}</p>
                  </div>
                  <div className="bg-[#F8FAFC] rounded-xl p-3">
                    <p className="text-xs text-gray-500">Investment</p>
                    <p className="font-semibold text-[#1E3A5F]">{tier.investment}</p>
                  </div>
                </div>
              </div>

              {/* Middle: Requirements */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Requirements</h3>
                <ul className="space-y-3 mb-6">
                  {tier.requirements.map((r, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: tier.color }} />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-gradient-to-br from-[#F8FAFC] to-white rounded-2xl p-4 border border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Best for</p>
                  <p className="text-sm text-[#1E3A5F]">{tier.bestFor}</p>
                </div>
              </div>

              {/* Right: Benefits + CTA */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">What you receive</h3>
                <ul className="space-y-3 mb-6">
                  {tier.benefits.map((b, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-sm text-gray-700">
                      <Sparkles className="w-4 h-4 mt-1 flex-shrink-0 text-[#FDB813]" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate(`/enquiries?tier=${encodeURIComponent(tier.name)}`)}
                  data-testid={`cert-tier-cta-${tier.name.toLowerCase()}`}
                  className="w-full bg-[#1E3A5F] hover:bg-[#152a45] text-white py-5 rounded-xl"
                >
                  Apply for {tier.name} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F] mb-4">Side-by-side comparison</h2>
            <p className="text-gray-600">Pick the tier that aligns with your scale, evidence base, and ambition.</p>
          </div>
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#1E3A5F] text-white">
                  <tr>
                    <th className="text-left px-6 py-4 font-semibold">Capability</th>
                    <th className="text-left px-6 py-4 font-semibold">Aspiring</th>
                    <th className="text-left px-6 py-4 font-semibold">Inspired</th>
                    <th className="text-left px-6 py-4 font-semibold">Luminary</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                      <td className="px-6 py-4 font-medium text-[#1E3A5F]">{row[0]}</td>
                      {row.slice(1).map((cell, j) => (
                        <td key={j} className="px-6 py-4 text-gray-700">
                          {cell === "—" ? <X className="w-4 h-4 text-gray-300" /> : cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Not sure which tier fits?</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Tell us about your organisation and we'll recommend the right starting point — usually within one business day.
          </p>
          <Button
            onClick={() => navigate("/contact")}
            data-testid="cert-tiers-contact-cta"
            className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-6 rounded-xl"
          >
            Get a Tier Recommendation <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default CertificationTiersPage;
